import { AdditiveBlending, CylinderGeometry, Mesh, MeshBasicMaterial, PlaneGeometry, TextureLoader } from "three";
import { convertToNumber3, RaycastLine, UNIT_Z } from "flavr-common";
import { Quaternion, Vector3 } from "flavr-math";
import { now } from "@messages/timing";
import { RequestRaycastClosestIntersectionMessage, ResponseRaycastClosestIntersectionMessage } from "@messages/selection";
import { localPlayerContainer } from "@modules/localplayer/LocalPlayerContainer";
import { RenderOrder } from "../RenderOrder";
import { FacetRenderer } from "../renderable/Renderable";
import { COLOR_FLAVIA_BLUE } from "../../../ui/style/predefined-colors";
import { configurePostprocessingOverlay } from "../postprocessing/PostprocessingOverlay";
const NEXT_RAYCAST_UPDATE_TIME = 1000 / 10;
const tmpVector31 = new Vector3();
const tmpVector32 = new Vector3();
const tmpVector33 = new Vector3();
const tempQuaternion1 = new Quaternion();
/**
 * The marker shows the normal orientation of the hit surface
 */
const textureLoader = new TextureLoader();
const HIT_MARKER_TEXTURE = textureLoader.load("img/ray_marker.png");
const HIT_MARKER_GEOMETRY = new PlaneGeometry(1, 1);
const HIT_MARKER_MATERIAL = new MeshBasicMaterial({
    map: HIT_MARKER_TEXTURE,
    transparent: true,
    blending: AdditiveBlending,
    depthTest: false,
    depthWrite: false
});
const HIT_MARKER_PROJECTED_SIZE = 0.0125;
/**
 * We use a cylinder that is oriented in the y-direction. We move the cylinder 0.5 units up such that its origin will be
 * located at the lower base of the cylinder. This makes orienting the line much easier
 */
const LINE_WIDTH = 0.005;
const LINE_GEOMETRY = new CylinderGeometry(LINE_WIDTH, LINE_WIDTH, 1);
LINE_GEOMETRY.translate(0, 0.5, 0); // Offset geometry such that its base is located at the start position of the ray
const LINE_MATERIAL = new MeshBasicMaterial({ color: COLOR_FLAVIA_BLUE, opacity: 0.5, transparent: true });
const LINE_BASE_DIRECTION = new Vector3(0, 1, 0);
export class RaycastLineRenderable extends FacetRenderer {
    constructor(owner) {
        super(owner);
        this.owner = owner;
        this.lastRaycastUpdate = 0;
        this.currentLength = 0;
        /// Perform raycast ///
        this.handleRaycastResponseMessage = (msg) => {
            if (this.facet === undefined)
                return;
            this.currentLength = Math.max(msg.response !== undefined ? msg.response.distance : this.facet.maxLength, Number.EPSILON);
            this.updateLinePosition(this.currentLength);
            this.updateMarker(this.facet, msg.response !== undefined ? msg.response.worldNormal : undefined, this.currentLength);
        };
    }
    destroy() {
        this.destroyLineInstance();
        if (this.hitPlane !== undefined) {
            this.owner.removeObjectFromPersistentRoot(this.hitPlane);
            this.hitPlane = undefined;
        }
        if (this.hitPlaneMaterial !== undefined) {
            this.hitPlaneMaterial.dispose();
            this.hitPlaneMaterial = undefined;
        }
        this.owner.renderer.removeAnimationCallback(this.owner.modelUID, RaycastLine);
    }
    setVisible(visible) {
        var _a;
        if (this.lineMesh !== undefined) {
            if (this.lineMesh.visible !== visible && ((_a = this.facet) === null || _a === void 0 ? void 0 : _a.autoLength) === true) {
                // if (visible) {
                //   this.owner.renderer.addAnimationCallback(this.owner.modelUID, RaycastLine, () => {
                //     this.update()
                //   })
                // } else {
                //   this.owner.renderer.removeAnimationCallback(this.owner.modelUID, RaycastLine)
                // }
            }
            this.lineMesh.visible = visible;
        }
        if (!visible) {
            this.hitPlane !== undefined && (this.hitPlane.visible = visible);
        }
    }
    updateFacet(facet) {
        var _a;
        // Create line if it doesn't exist yet
        if (this.lineMesh === undefined) {
            this.lineMesh = this.createLineInstance();
        }
        // Update line color
        ;
        this.lineMesh.material.color.setHex(facet.colorHex);
        if (this.hitPlaneMaterial !== undefined) {
            this.hitPlaneMaterial.color.setHex(facet.colorHex);
        }
        if (!facet.autoLength) {
            this.updateMarker(facet);
        }
        // We add an animation callback to automatically update the line length if autoLength is true
        if (this.lineMesh.visible) {
            if (facet.autoLength) {
                if (this.facet === undefined || !this.facet.autoLength) {
                    this.owner.renderer.addAnimationCallback(this.owner.modelUID, RaycastLine, () => {
                        this.update();
                    });
                }
            }
            else if (((_a = this.facet) === null || _a === void 0 ? void 0 : _a.autoLength) === true) {
                this.owner.renderer.removeAnimationCallback(this.owner.modelUID, RaycastLine);
            }
        }
    }
    updateTransform() {
        if (this.facet !== undefined)
            this.updateLinePosition(this.facet.autoLength ? this.currentLength : this.facet.maxLength);
        if (this.hitPlane !== undefined) {
            this.hitPlane.matrixWorld.multiplyMatrices(this.owner.matrixWorld, this.hitPlane.matrix);
            this.hitPlane.matrixWorldNeedsUpdate = false;
        }
    }
    /// Update line and marker ///
    update() {
        if (this.facet === undefined)
            return;
        if (now() - this.lastRaycastUpdate <= NEXT_RAYCAST_UPDATE_TIME)
            return;
        this.lastRaycastUpdate = now();
        const { originWorld, directionWorld } = this.computeRayWorld(this.facet);
        this.owner.renderer
            .getConnection()
            .askByMessageType(new RequestRaycastClosestIntersectionMessage(convertToNumber3(originWorld), convertToNumber3(directionWorld)), ResponseRaycastClosestIntersectionMessage)
            .then(this.handleRaycastResponseMessage)
            .catch(e => { console.error(e); });
    }
    updateLinePosition(length) {
        if (this.facet === undefined || this.lineMesh === undefined)
            return;
        const { originWorld, directionWorld } = this.computeRayWorld(this.facet);
        this.lineMesh.scale.set(1, length * localPlayerContainer.getPlatformScale(), 1);
        this.lineMesh.position.copy(originWorld);
        this.lineMesh.quaternion.setFromUnitVectors(LINE_BASE_DIRECTION, directionWorld);
    }
    updateMarker(facet, hitNormal = facet.hitNorm, length = facet.maxLength) {
        var _a;
        if (this.hitPlaneMaterial === undefined) {
            this.hitPlaneMaterial = HIT_MARKER_MATERIAL.clone();
            if (this.facet !== undefined) {
                this.hitPlaneMaterial.color.setHex(this.facet.colorHex);
            }
        }
        if (this.hitPlane === undefined) {
            this.hitPlane = new Mesh(HIT_MARKER_GEOMETRY, this.hitPlaneMaterial);
            configurePostprocessingOverlay(this.hitPlane);
            this.hitPlane.matrixAutoUpdate = false;
            this.hitPlane.renderOrder = RenderOrder.LASER;
            this.owner.addObjectToPersistentRoot(this.hitPlane);
        }
        if (hitNormal !== undefined) {
            this.hitPlane.position
                .fromArray(facet.direction)
                .multiplyScalar(length - 0.01)
                .divide(this.owner.worldScale)
                .multiplyScalar(localPlayerContainer.getPlatformScale())
                .add(tmpVector33.fromArray(facet.origin));
            const localNorm = tmpVector31
                .fromArray(hitNormal)
                .applyQuaternion(tempQuaternion1.copy(this.owner.worldOrientation).invert());
            this.hitPlane.quaternion.setFromUnitVectors(UNIT_Z, localNorm);
            // this.hitPlane.position is in the coordinate system of the model and virtualPlatform.cameraWorldPosition is in
            // world coordinates. To get the distance between these two point we have to convert them into the same coordinate
            // system
            const distance = localPlayerContainer
                .cameraWorldPosition.distanceTo(tmpVector32.copy(this.hitPlane.position).applyMatrix4(this.owner.matrixWorld));
            // We scale the marker in a way that it's projected size is always the same
            this.hitPlane.scale.setScalar(HIT_MARKER_PROJECTED_SIZE * (distance + 1));
            this.hitPlane.updateMatrix();
            this.hitPlane.matrixWorld.multiplyMatrices(this.owner.matrixWorld, this.hitPlane.matrix);
            this.hitPlane.matrixWorldNeedsUpdate = false;
        }
        this.hitPlane.visible = hitNormal !== undefined && ((_a = this.lineMesh) === null || _a === void 0 ? void 0 : _a.visible) === true;
    }
    computeRayWorld(facet) {
        const originWorld = tmpVector31
            .fromArray(facet.origin)
            .applyMatrix4(this.owner.matrixWorld);
        const directionWorld = tmpVector32
            .fromArray(facet.direction)
            .applyQuaternion(this.owner.worldOrientation)
            .normalize();
        return { originWorld, directionWorld };
    }
    /// Create line geometry ///
    createLineInstance() {
        this.lineMaterial = LINE_MATERIAL.clone();
        const lineMesh = new Mesh(LINE_GEOMETRY, this.lineMaterial);
        lineMesh.renderOrder = RenderOrder.LASER;
        this.owner.addObjectToPersistentRoot(lineMesh);
        return lineMesh;
    }
    destroyLineInstance() {
        if (this.lineMesh !== undefined) {
            this.owner.removeObjectFromPersistentRoot(this.lineMesh);
            this.lineMesh = undefined;
        }
        if (this.lineMaterial !== undefined) {
            this.lineMaterial.dispose();
            this.lineMaterial = undefined;
        }
    }
}
